import { t } from "../../i18n";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  FolderMinus,
  FolderPlus,
  Folder,
  Pin,
  PinOff,
  Trash2,
  X,
} from "lucide-react";
import type { Conversation, ConversationGroup } from "@lumen/shared";

interface MenuPosition {
  left: number;
  /** Popup opens above the anchor. */
  bottom?: number;
  /** Popup opens below the anchor (flipped when there is more room). */
  top?: number;
  width: number;
  maxHeight: number;
}

function computePosition(rect: DOMRect): MenuPosition {
  const width = Math.min(288, window.innerWidth - 16);
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  const spaceAbove = rect.top - 16;
  const spaceBelow = window.innerHeight - rect.bottom - 16;
  const flipBelow = spaceBelow >= spaceAbove || spaceAbove < 120;
  if (flipBelow)
    return {
      left,
      top: rect.bottom + 8,
      width,
      maxHeight: Math.max(80, Math.min(512, spaceBelow)),
    };
  return {
    left,
    bottom: window.innerHeight - rect.top + 8,
    width,
    maxHeight: Math.max(80, Math.min(512, spaceAbove)),
  };
}

function samePosition(a: MenuPosition | null, b: MenuPosition) {
  return (
    a !== null &&
    a.left === b.left &&
    a.bottom === b.bottom &&
    a.top === b.top &&
    a.width === b.width &&
    a.maxHeight === b.maxHeight
  );
}

export function ConversationActionMenu({
  open,
  onClose,
  anchorRef,
  conversation,
  groups,
  projects,
  onTogglePin,
  onMoveToGroup,
  onCreateGroup,
  onMoveToProject,
  onDelete,
}: {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  conversation: Conversation;
  groups: ConversationGroup[];
  projects: Array<{ id: string; name: string }>;
  onTogglePin: () => void;
  onMoveToGroup: (groupId: string | null) => void;
  onCreateGroup: (name: string) => Promise<ConversationGroup | null>;
  onMoveToProject: (projectId: string) => void;
  onDelete: () => void;
}) {
  const [view, setView] = useState<"root" | "groups" | "projects">("root");
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [creatingBusy, setCreatingBusy] = useState(false);
  const [createError, setCreateError] = useState("");
  const createInputRef = useRef<HTMLInputElement | null>(null);

  // Position the popup while open and keep it anchored on resize/scroll.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const update = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition(computePosition(rect));
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, anchorRef]);

  // Re-measure whenever the sidebar reorders (e.g. the 1s conversation poll
  // replacing `conversations`, which changes the `conversation` prop identity
  // and can move the anchor row) while the menu is open. Kept separate from
  // the listener effect so resize/scroll listeners are not re-registered on
  // every poll.
  useLayoutEffect(() => {
    if (!open) return;
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition((current) => {
      const next = computePosition(rect);
      return samePosition(current, next) ? current : next;
    });
  }, [open, anchorRef, conversation]);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !anchorRef.current?.contains(target) &&
        !popupRef.current?.contains(target)
      ) {
        onClose();
      }
    };
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [open, anchorRef, onClose]);

  // Reset the drill-down view whenever the menu closes.
  useEffect(() => {
    if (!open) setView("root");
  }, [open]);

  // Focus the new-group name field as soon as it is revealed.
  useEffect(() => {
    if (creatingGroup) createInputRef.current?.focus();
  }, [creatingGroup]);

  // Move focus into the menu so keyboard users can operate it.
  useEffect(() => {
    if (!open || !position) return;
    popupRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open, position, view]);

  const submitCreateGroup = async () => {
    const name = newGroupName.trim();
    if (!name || creatingBusy) return;
    setCreatingBusy(true);
    setCreateError("");
    const created = await onCreateGroup(name);
    setCreatingBusy(false);
    if (!created) {
      setCreateError(t("Could not create group."));
      return;
    }
    // The user opened "Move into" for this conversation, so drop it straight
    // into the freshly created group.
    onMoveToGroup(created.id);
    onClose();
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const { key } = event;
    if (
      key !== "ArrowDown" &&
      key !== "ArrowUp" &&
      key !== "Home" &&
      key !== "End"
    )
      return;
    const items = Array.from(
      popupRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ??
        [],
    ).filter((element) => !(element as HTMLButtonElement).disabled);
    if (!items.length) return;
    const currentIndex = items.indexOf(document.activeElement as HTMLElement);
    let nextIndex: number;
    if (key === "ArrowDown")
      nextIndex =
        currentIndex < 0 || currentIndex === items.length - 1
          ? 0
          : currentIndex + 1;
    else if (key === "ArrowUp")
      nextIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
    else if (key === "Home") nextIndex = 0;
    else nextIndex = items.length - 1;
    items[nextIndex]?.focus();
    event.preventDefault();
  };

  if (!open || !position) return null;

  const style = {
    "--tool-popup-left": `${position.left}px`,
    "--tool-popup-bottom":
      position.bottom !== undefined ? `${position.bottom}px` : undefined,
    "--tool-popup-top":
      position.top !== undefined ? `${position.top}px` : undefined,
    "--tool-popup-width": `${position.width}px`,
    "--tool-popup-max-height": `${position.maxHeight}px`,
  } as CSSProperties;

  const backButton = (title: string) => (
    <button
      type="button"
      className="button p-1.5"
      onClick={() => setView("root")}
      title={title}
      aria-label={title}
    >
      <ArrowLeft size={16} />
    </button>
  );

  return createPortal(
    <div
      ref={popupRef}
      style={style}
      role="menu"
      aria-labelledby={anchorRef.current?.id}
      onKeyDown={onMenuKeyDown}
      className={`tool-popup-responsive fixed z-[100] overflow-y-auto border border-stone-200 bg-white p-2 shadow-2xl dark:border-white/10 dark:bg-[#222725] ${position.top !== undefined ? "tool-popup-below" : ""}`}
    >
      {view === "groups" ? (
        <>
          <div className="flex items-center gap-2 border-b border-stone-100 px-1 pb-2 dark:border-white/10">
            {backButton(t("Move into"))}
            <div className="min-w-0 flex-1">
              <strong className="block truncate text-sm">
                {t("Move into")}
              </strong>
            </div>
            <button
              type="button"
              className="button p-1.5 sm:hidden"
              onClick={onClose}
              aria-label={t("Close")}
            >
              <X size={18} />
            </button>
          </div>
          <div className="p-1">
            {groups.length === 0 && !creatingGroup && (
              <p className="p-2 text-xs text-stone-400">
                {t("No groups yet.")}
              </p>
            )}
            {groups.map((group) => (
              <button
                key={group.id}
                type="button"
                role="menuitem"
                className={`flex w-full items-center gap-2.5 rounded-lg p-2 text-left text-sm hover:bg-stone-50 dark:hover:bg-white/5 ${conversation.groupId === group.id ? "font-medium text-lumen-800 dark:text-lumen-200" : "text-stone-600 dark:text-stone-300"}`}
                onClick={() => {
                  onMoveToGroup(group.id);
                  onClose();
                }}
              >
                <span className="min-w-0 flex-1 truncate">{group.name}</span>
                {conversation.groupId === group.id && (
                  <Check
                    size={15}
                    className="shrink-0 text-lumen-600 dark:text-lumen-300"
                  />
                )}
              </button>
            ))}
            <button
              type="button"
              role="menuitem"
              disabled={conversation.groupId === null}
              className={`flex w-full items-center gap-2.5 rounded-lg p-2 text-left text-sm ${conversation.groupId === null ? "cursor-not-allowed text-stone-300 dark:text-stone-600" : "text-stone-600 hover:bg-stone-50 dark:text-stone-300 dark:hover:bg-white/5"}`}
              onClick={() => {
                onMoveToGroup(null);
                onClose();
              }}
            >
              <FolderMinus size={15} className="shrink-0 text-stone-400" />
              <span className="min-w-0 flex-1">{t("No group")}</span>
            </button>
            <div className="mt-1 border-t border-stone-100 pt-1 dark:border-white/10">
              {creatingGroup ? (
                <div className="flex items-center gap-1.5 p-1">
                  <input
                    ref={createInputRef}
                    type="text"
                    value={newGroupName}
                    onChange={(event) => setNewGroupName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") void submitCreateGroup();
                      if (event.key === "Escape") {
                        setCreatingGroup(false);
                        setNewGroupName("");
                        setCreateError("");
                      }
                    }}
                    placeholder={t("Group name")}
                    aria-label={t("Group name")}
                    className="field w-full min-w-0 px-2 py-1.5 text-sm"
                  />
                  <button
                    type="button"
                    className="button shrink-0 p-1.5"
                    disabled={!newGroupName.trim() || creatingBusy}
                    onClick={() => void submitCreateGroup()}
                    title={t("Create group")}
                    aria-label={t("Create group")}
                  >
                    <Check size={14} />
                  </button>
                  <button
                    type="button"
                    className="button shrink-0 p-1.5"
                    disabled={creatingBusy}
                    onClick={() => {
                      setCreatingGroup(false);
                      setNewGroupName("");
                      setCreateError("");
                    }}
                    title={t("Cancel")}
                    aria-label={t("Cancel")}
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2.5 rounded-lg p-2 text-left text-sm text-stone-500 hover:bg-stone-50 hover:text-lumen-700 dark:text-stone-400 dark:hover:bg-white/5 dark:hover:text-lumen-300"
                  onClick={() => setCreatingGroup(true)}
                >
                  <FolderPlus
                    size={15}
                    className="shrink-0 text-lumen-600 dark:text-lumen-300"
                  />
                  <span className="min-w-0 flex-1">{t("New group")}</span>
                </button>
              )}
              {createError && (
                <p className="mt-1 px-2 text-xs text-red-600 dark:text-red-300">
                  {createError}
                </p>
              )}
            </div>
          </div>
        </>
      ) : view === "projects" ? (
        <>
          <div className="flex items-center gap-2 border-b border-stone-100 px-1 pb-2 dark:border-white/10">
            {backButton(t("Move into project"))}
            <div className="min-w-0 flex-1">
              <strong className="block truncate text-sm">
                {t("Move into project")}
              </strong>
            </div>
            <button
              type="button"
              className="button p-1.5 sm:hidden"
              onClick={onClose}
              aria-label={t("Close")}
            >
              <X size={18} />
            </button>
          </div>
          <div className="p-1">
            {projects.length === 0 && (
              <p className="p-2 text-xs text-stone-400">
                {t("No projects yet.")}
              </p>
            )}
            {projects.map((project) => (
              <button
                key={project.id}
                type="button"
                role="menuitem"
                className={`flex w-full items-center gap-2.5 rounded-lg p-2 text-left text-sm hover:bg-stone-50 dark:hover:bg-white/5 ${conversation.groupId === project.id ? "font-medium text-lumen-800 dark:text-lumen-200" : "text-stone-600 dark:text-stone-300"}`}
                onClick={() => {
                  onMoveToProject(project.id);
                  onClose();
                }}
              >
                <Folder
                  size={15}
                  className="shrink-0 text-lumen-600 dark:text-lumen-300"
                />
                <span className="min-w-0 flex-1 truncate">{project.name}</span>
                {conversation.groupId === project.id && (
                  <Check
                    size={15}
                    className="shrink-0 text-lumen-600 dark:text-lumen-300"
                  />
                )}
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 border-b border-stone-100 px-1 pb-2 dark:border-white/10">
            <div className="min-w-0 flex-1">
              <strong className="block truncate text-sm">
                {t("Conversation actions")}
              </strong>
            </div>
            <button
              type="button"
              className="button p-1.5 sm:hidden"
              onClick={onClose}
              aria-label={t("Close")}
            >
              <X size={18} />
            </button>
          </div>
          <div className="p-1">
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2.5 rounded-lg p-2 text-left text-sm text-stone-600 hover:bg-stone-50 dark:text-stone-300 dark:hover:bg-white/5"
              onClick={() => {
                onTogglePin();
                onClose();
              }}
            >
              {conversation.pinned ? (
                <PinOff
                  size={15}
                  className="shrink-0 text-lumen-600 dark:text-lumen-300"
                />
              ) : (
                <Pin
                  size={15}
                  className="shrink-0 text-lumen-600 dark:text-lumen-300"
                />
              )}
              <span className="min-w-0 flex-1">
                {conversation.pinned
                  ? t("Unpin conversation")
                  : t("Pin conversation")}
              </span>
            </button>
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2.5 rounded-lg p-2 text-left text-sm text-stone-600 hover:bg-stone-50 dark:text-stone-300 dark:hover:bg-white/5"
              onClick={() => setView("groups")}
            >
              <span className="min-w-0 flex-1">{t("Move into")}</span>
              <ChevronRight size={15} className="shrink-0 text-stone-400" />
            </button>
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2.5 rounded-lg p-2 text-left text-sm text-stone-600 hover:bg-stone-50 dark:text-stone-300 dark:hover:bg-white/5"
              onClick={() => setView("projects")}
            >
              <span className="min-w-0 flex-1">{t("Move into project")}</span>
              <ChevronRight size={15} className="shrink-0 text-stone-400" />
            </button>
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2.5 rounded-lg p-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/30"
              onClick={() => {
                onDelete();
                onClose();
              }}
            >
              <Trash2 size={15} className="shrink-0" />
              <span className="min-w-0 flex-1">{t("Delete")}</span>
            </button>
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}
